import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as loginApi, getUserInfo } from '@/api/system'
import type { UserInfo } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string>(localStorage.getItem('token') || '')
  const refreshToken = ref<string>(localStorage.getItem('refreshToken') || '')
  const userInfo = ref<UserInfo | null>(null)

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const roles = computed(() => userInfo.value?.roles || [])
  const shopIds = computed(() => userInfo.value?.shopIds || [])

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
    localStorage.setItem('token', newToken)
  }

  const setRefreshToken = (newToken: string) => {
    refreshToken.value = newToken
    localStorage.setItem('refreshToken', newToken)
  }

  const clearToken = () => {
    token.value = ''
    refreshToken.value = ''
    userInfo.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
  }

  const login = async (username: string, password: string, tenantId?: number) => {
    const res = await loginApi({ username, password, ...(tenantId ? { tenantId } : {}) })
    // 多企业归属：后端要求先选择企业，直接返回由页面处理
    if (res.data?.needTenant) return res
    const user = res.data.user
    // 优先使用后端返回的 roles 数组，兼容旧版单 role 字段
    const roles = user.roles || (Array.isArray(user.role) ? user.role : user.role ? [user.role] : [])
    setToken(res.data.token)
    if (res.data.refreshToken) setRefreshToken(res.data.refreshToken)
    userInfo.value = {
      ...user,
      roles
    }
    return res
  }

  const fetchUserInfo = async () => {
    if (!token.value) return
    const res = await getUserInfo()
    const user = res.data
    // 优先使用后端返回的 roles 数组，兼容旧版单 role 字段
    const roles = user.roles || (Array.isArray(user.role) ? user.role : user.role ? [user.role] : [])
    userInfo.value = {
      ...user,
      roles
    }
  }

  const logout = (redirect?: string) => {
    clearToken()
    const isMobile = window.location.pathname.includes('/mobile.html')
    if (isMobile) {
      // replace 避免污染 WebView 历史栈（APP 内跨文档跳转会导致返回手势失效）
      window.location.replace('/mobile.html#/login')
      return
    }
    const target = redirect || (window.location.pathname + window.location.search)
    const suffix = target && target !== '/login' ? `?redirect=${encodeURIComponent(target)}` : ''
    window.location.replace(`/login${suffix}`)
  }

  return {
    token,
    refreshToken,
    userInfo,
    isLoggedIn,
    roles,
    shopIds,
    setToken,
    setRefreshToken,
    clearToken,
    login,
    fetchUserInfo,
    logout
  }
})
